import { handleActions } from 'redux-actions';
import * as inflection from 'inflection';

import log, { error } from 'utils/logger' // eslint-disable-line


const handleError = (state, action) => {
  const { payload } = action

  let errors = [];

  if (!(payload && payload.obj)) {
    log("API reducers empty response error.", action)
    errors.push(`API response is empty for URL: ${payload.url}`);
  } else if(Array.isArray(payload.obj.error)) {
    errors = payload.obj.error;
  } else {
    errors.push(payload.obj.error);
  }

  return Object.assign({}, state, { errors });

};

// Request state transition reducers. These are generic reducers that update API
// request related metadata for a given action type.
//
// For example, FEEDS_GET leads to corresponding FEEDS_GET_PENDING and
// FEEDS_GET_FULFILLED actions that directly update stored data (feed records),
// these reducers update stored meta-data. Request time, error messages,
// pagination headers, etc.
const handleRequestState = {
  pending(key) {
    return (state) => {
      return Object.assign({}, state, {
        [ key ]: Object.assign({}, state[key], {
          loading: true,
          state: 'pending',
          started_at: new Date(),
          error: null
        })
      })
    }
  },

  fulfilled(key) {
    return (state, payload) => {
      const pagination = {
        start_time: payload.headers['x-pagination-start'],
        end_time: payload.headers['x-pagination-end'],
        limit: payload.headers['x-pagination-limit'],
        count: payload.headers['x-pagination-count'],
      }

      if (pagination.limit) pagination.limit = parseInt(pagination.limit)
      if (pagination.count) pagination.count = parseInt(pagination.count)

      return Object.assign({}, state, {
        [ key ]: Object.assign({}, state[key], {
          loading: false,
          state: 'fulfilled',
          completed_at: new Date(),
          pagination: pagination,
          error: null
        })
      })
    }
  },

  rejected(key) {
    return (state, error) => {
      return Object.assign({}, state, {
        [ key ]: Object.assign({}, state[key], {
          loading: false,
          state: 'rejected',
          completed_at: new Date(),
          error
        })
      })
    }
  }
}

export const buildAPIReducers = (name, initialState, transformCb) => {

  let transform = transformCb || function (record) { return record }

  // action types
  // const SINGLE = inflection.singularize(name).toUpperCase();
  const PLURAL = inflection.pluralize(name).toUpperCase();

  // storage locations
  const single = inflection.singularize(name).toLowerCase();
  const plural = inflection.pluralize(name).toLowerCase();
  const single_meta = `${single}_meta`
  const plural_meta = `${plural}_meta`

  // state modifiers
  const request = ['pending', 'fulfilled', 'rejected'].reduce((obj, method) => {
    return Object.assign(obj, {
      [method]: {
        collection: handleRequestState[method](plural_meta),
        member: handleRequestState[method](single_meta)
      }
    })
  }, {})

  // used by ALL APIs to merge new records
  const hashKey = record => {
    if (record.id && record.key)
      return `${record.id}-${record.key}`
    else if (record.key)
      return record.key
    else
      return record.id
  }

  const reducers = {
    [`${PLURAL}_CLEAR_CURRENT`]: (state) => {
      return Object.assign({}, state, { [single]: {} })
    },

    [`${PLURAL}_ALL_PENDING`]: (state) => { return request.pending.collection(state) },
    [`${PLURAL}_ALL_FULFILLED`]: (state, { payload }) => {
      let next = state[plural]

      if (Array.isArray(next) && Array.isArray(payload.object)) {
        // create hash of existing records by id + key if available
        const byID = next.reduce((obj, rec) => {
          obj[hashKey(rec)] = rec
          return obj
        }, {})

        // add newly accessed records to hash, merging records that already exist
        payload.object.forEach(record => {
          let key = hashKey(record)
          if (key in byID) {
            // shallow merge
            byID[key] = Object.assign({}, byID[key], transform(record))
          } else {
            // new record
            byID[key] = transform(record)
          }
        })

        // get values from byID, sorted by system order
        next = payload.object.map(record => byID[hashKey(record)])
      } else if (!Array.isArray(next) && Array.isArray(payload.object)) {
        // no records exist, just use payload
        next = payload.object
      }

      return Object.assign({}, request.fulfilled.collection(state, payload), {
        [plural]: next,
      });
    },

    [`${PLURAL}_SELECT`]: (state, { payload }) => {
      const copy = Object.assign({}, payload);
      return Object.assign({}, state, { [single]: copy });
    },

    [`${PLURAL}_GET_PENDING`]: (state) => { return request.pending.member(state) },
    [`${PLURAL}_GET_FULFILLED`]: (state, { payload }) => {
      const copy = Object.assign({}, state[single], payload.object);
      return Object.assign({}, request.fulfilled.member(state, payload), { [single]: copy });
    },

    [`${PLURAL}_REPLACE_PENDING`]: (state) => { return request.pending.member(state) },
    [`${PLURAL}_REPLACE_FULFILLED`]: (state, { payload }) => {
      // replace updated record
      const all = state[plural].map((record) => {
        if (record.id === payload.object.id) {
          return Object.assign({}, record, payload.object)
        } else {
          return Object.assign({}, record)
        }
      });

      let current = state[name],
          singular = current;
      if (current.id === payload.object.id) {
        singular = Object.assign({}, current, payload.object)
      }

      return Object.assign({}, request.fulfilled.collection(state, payload), { [name]: singular, [plural]: all });
    },

    [`${PLURAL}_CREATE_PENDING`]: (state) => { return request.pending.collection(state) },
    [`${PLURAL}_CREATE_FULFILLED`]: (state, { payload }) => {

      const all = state[plural].concat(payload.object);

      all.sort((a, b) => {
        if (a.id > b.id) {
          return -1;
        } else if (a.id < b.id) {
          return 1;
        } else {
          return 0;
        }
      });

      return Object.assign({}, request.fulfilled.collection(state, payload), { [plural]: all });

    },

    [`${PLURAL}_DESTROY_PENDING`]: (state) => { return request.pending.collection(state) },
    [`${PLURAL}_DESTROY_FULFILLED`]: (state, { payload }) => {

      const all = state[plural].filter((rec) => {
        return rec.id !== payload.object.id;
      });

      return Object.assign({}, request.fulfilled.collection(state, payload), { [plural]: all });

    },

    [`${PLURAL}_RESET_ERRORS`]: (state) => {
      return Object.assign({}, state, { errors: null });
    }

  };

  // details is get with extra fields
  reducers[`${PLURAL}_DETAILS_PENDING`] = reducers[`${PLURAL}_GET_PENDING`]
  reducers[`${PLURAL}_DETAILS_FULFILLED`] = reducers[`${PLURAL}_GET_FULFILLED`]

  const handler = handleActions(reducers, initialState);

  // intercept and handle all _REJECTED actions the same
  const rejectedMatcher = new RegExp(`^${PLURAL}_([A-Z_]+)_REJECTED$`)

  return (state, action) => {
    if (rejectedMatcher.test(action.type)) {
      const match = rejectedMatcher.exec(action.type)
      const method = match[1]
      let requestReducer = (method === 'GET') ?
        request.rejected.member :
        request.rejected.collection

      return handleError(requestReducer(state, action.payload), action)
    } else {
      return handler(state, action)
    }
  }

};
